const UPLOAD_FILE_ENDPOINT = `${process.env.REACT_APP_Auth_Url}/api/MomentumAMP/upload-file`;
const UPLOAD_AMS_FILES_ENDPOINT = `${process.env.REACT_APP_Auth_Url}/api/MomentumAMP/upload-ams-files`;
const CHANNEL_FILE_ENDPOINT = `${process.env.REACT_APP_Auth_Url}/api/momentumamp/channel-file`;

export interface FileUploadResponse {
  fileUrl: string;
  fileName: string;
}

/** One folder's worth of selected AMS files — the upload API lists a single
 *  folder and filters by file ids, so multi-folder selections are grouped. */
export interface AmsFileSelection {
  insuredId: string;
  folderId: string;
  amsFileIds: string[];
}

export interface AmsAttachmentRef {
  name: string;
  url: string;
  /** File size in bytes, as reported by the server-side download. */
  size?: number;
}

// Uploads AMS-drive selections server-side (the AMS pre-signed download URLs
// are not CORS-enabled, so the browser cannot fetch the bytes itself): the API
// resolves the NowCerts download URLs, fetches the bytes and stores them in the
// same blob path as uploadFile. Returns plain {name, url} refs the chat
// composers can attach directly.
export async function uploadAmsFiles(
  selections: AmsFileSelection[],
  conversationId: string,
): Promise<AmsAttachmentRef[]> {
  const authToken = localStorage.getItem('authToken');
  const ncToken = localStorage.getItem('amsToken') || '';
  const out: AmsAttachmentRef[] = [];

  for (const selection of selections) {
    const response = await fetch(`${UPLOAD_AMS_FILES_ENDPOINT}/${conversationId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken ?? ''}`,
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...selection, ncToken }),
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    const data = await response.json().catch(() => []);
    for (const f of Array.isArray(data) ? data : []) {
      if (typeof f?.fileName === 'string' && f.fileName && typeof f?.fileUrl === 'string' && f.fileUrl) {
        out.push({
          name: f.fileName,
          url: f.fileUrl,
          size: typeof f?.fileSize === 'number' && f.fileSize > 0 ? f.fileSize : undefined,
        });
      }
    }
  }

  if (out.length === 0) {
    throw new Error('No files could be uploaded from the AMS drive.');
  }
  return out;
}


export async function uploadFile(
  file: File,
  conversationId: string
): Promise<FileUploadResponse> {
  const authToken = localStorage.getItem('authToken');

  const formData = new FormData();
  formData.append('formFile', file);

  const url = `${UPLOAD_FILE_ENDPOINT}/${conversationId}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken ?? ''}`,
        Accept: 'application/json, text/plain, */*',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    const data: FileUploadResponse = await response.json();
    return data;
  } catch (error) {
    console.error('File upload error:', error);
    throw new Error('Failed to upload file. Please try again.');
  }
}


export async function uploadChannelFile(file: File, channelId: string): Promise<FileUploadResponse> {
  const authToken = localStorage.getItem('authToken');

  const formData = new FormData();
  formData.append('transactionId', crypto.randomUUID());
  formData.append('channelId', channelId);
  formData.append('formFile', file);

  try {
    const response = await fetch(CHANNEL_FILE_ENDPOINT, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken ?? ''}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    const data: FileUploadResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Channel file upload error:', error);
    throw new Error('Failed to upload file. Please try again.');
  }
}

export async function uploadMultipleFiles(
  files: File[],
  conversationId: string
): Promise<FileUploadResponse[]> {
  try {
    const uploadPromises = files.map(file => uploadFile(file, conversationId));
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Multiple file upload error:', error);
    throw error;
  }
}
